// Client for the org licensing API (/api/org-licensing). IT admins sign in with an email code; the web session is
// stored under its own key (`orgLicSession`) so it never collides with the platform `jwttoken`. Vavetek admins use
// the platform admin JWT instead ("super-admin" mode).
import { createContext, useContext } from 'react';

export class LicApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'LicApiError';
    this.status = status;
    this.code = body?.code;
    this.body = body || {};
  }
}

// Sign-in pages: IT admins use the IT admin tab of the shared /login page; Vavetek admins have their own page.
export const IT_ADMIN_LOGIN_PATH = '/login?as=it-admin';
export const SUPER_ADMIN_LOGIN_PATH = '/superadmin';

const SESSION_KEY = 'orgLicSession';
const apiUrl = (path) => `${process.env.REACT_APP_API_BASE_URL}/api/org-licensing/${path.replace(/^\//, '')}`;

// ------------------------------------------------------------------ session storage

/** The IT-admin web session: { sessionToken, expiresAtUtc, email, adminOrgs }. Null when missing or expired. */
export const getLicSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const session = raw ? JSON.parse(raw) : null;
    if (!session?.sessionToken) {
      return null;
    }
    const expires = session.expiresAtUtc ? new Date(session.expiresAtUtc).getTime() : NaN;
    if (!Number.isNaN(expires) && expires <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return { ...session, adminOrgs: Array.isArray(session.adminOrgs) ? session.adminOrgs : [] };
  } catch (error) {
    return null;
  }
};

const saveSession = (session) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    // Storage can be unavailable (private mode); the user will just have to sign in again.
  }
};

export const clearLicSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (error) {
    // Nothing to clear.
  }
};

/** The Vavetek admin platform JWT (set by AdminLogin). */
export const getAdminToken = () => localStorage.getItem('jwttoken') || sessionStorage.getItem('jwttoken');

const clearAdminToken = () => {
  localStorage.removeItem('jwttoken');
  sessionStorage.removeItem('jwttoken');
};

// ------------------------------------------------------------------ fetch helpers

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
};

const fallbackMessage = (status) => {
  if (status === 429) {
    return 'Too many attempts. Wait a few minutes and try again.';
  }
  if (status === 403) {
    return 'You do not have access to this.';
  }
  return `Request failed (${status}).`;
};

const toError = (response, body) =>
  new LicApiError(body?.message || fallbackMessage(response.status), response.status, body);

const send = async (path, { method = 'GET', body, query, token, headers } = {}) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const url = params.toString() ? `${apiUrl(path)}?${params}` : apiUrl(path);

  try {
    return await fetch(url, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(headers || {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new LicApiError('Could not reach the licensing service.', 0);
  }
};

/** Unauthenticated POST (sign-in). A 401 here means a wrong code, not an ended session, so it never redirects. */
const post = async (path, body) => {
  const response = await send(path, { method: 'POST', body });
  const result = await parseBody(response);
  if (!response.ok || result.success === false) {
    throw toError(response, result);
  }
  return result;
};

// ------------------------------------------------------------------ sign-in

export const requestCode = (email) => post('otp/request', { email, purpose: 'web' });

export const verifyCode = async (email, code) => {
  const result = await post('otp/verify', { email, code, purpose: 'web' });
  const session = {
    sessionToken: result.sessionToken,
    expiresAtUtc: result.expiresAtUtc || null,
    email: result.email || email,
    adminOrgs: Array.isArray(result.adminOrgs) ? result.adminOrgs : [],
  };
  if (!session.sessionToken) {
    throw new LicApiError('Sign-in failed: the server did not return a session.', 500, result);
  }
  saveSession(session);
  return session;
};

/** Revokes the web session on the server (best effort) and forgets it locally. */
export const signOut = async () => {
  const session = getLicSession();
  if (session) {
    try {
      await send('signout', { method: 'POST', token: session.sessionToken });
    } catch (error) {
      // Signing out locally is enough if the server cannot be reached.
    }
  }
  clearLicSession();
};

// ------------------------------------------------------------------ API client

/**
 * Creates an org licensing API client.
 *  - superAdmin: send the Vavetek admin JWT instead of the web session; a 401 goes to the admin login.
 *  - otherwise: send the web session token; a 401 clears the session and goes to the org-licensing login.
 */
export const createLicApi = ({ superAdmin = false } = {}) => {
  const onUnauthorized = () => {
    if (superAdmin) {
      clearAdminToken();
      window.location.href = SUPER_ADMIN_LOGIN_PATH;
    } else {
      clearLicSession();
      window.location.href = IT_ADMIN_LOGIN_PATH;
    }
  };

  /** Calls the API. Throws LicApiError (with `status`, `code`, `body`); a 401 signs the user out. */
  const licFetch = async (path, { method = 'GET', body, query, headers } = {}) => {
    const token = superAdmin ? getAdminToken() : getLicSession()?.sessionToken;
    if (!token) {
      onUnauthorized();
      throw new LicApiError('Please sign in again.', 401);
    }

    const response = await send(path, { method, body, query, token, headers });
    const result = await parseBody(response);

    if (response.status === 401) {
      onUnauthorized();
      throw new LicApiError(result.message || 'Your session has ended. Please sign in again.', 401, result);
    }
    if (!response.ok || result.success === false) {
      throw toError(response, result);
    }
    return result;
  };

  return { licFetch, superAdmin };
};

/** The signed-in IT admin and the orgs they administer. Also refreshes `adminOrgs` in the stored session. */
export const getMe = async (api = createLicApi()) => {
  const me = await api.licFetch('me');
  const session = getLicSession();
  if (session && !api.superAdmin) {
    saveSession({
      ...session,
      email: me.email || session.email,
      adminOrgs: Array.isArray(me.adminOrgs) ? me.adminOrgs : session.adminOrgs,
    });
  }
  return { email: me.email || session?.email || '', adminOrgs: Array.isArray(me.adminOrgs) ? me.adminOrgs : [] };
};

export const LicApiContext = createContext(createLicApi());

export const useLicApi = () => useContext(LicApiContext);

// ------------------------------------------------------------------ paths

const enc = encodeURIComponent;

export const orgPath = (orgId, rest = '') => `orgs/${enc(orgId)}${rest}`;
export const seatReleasePath = (orgId, email) => orgPath(orgId, `/seats/${enc(email)}/release`);
export const memberPath = (orgId, email, rest = '') => orgPath(orgId, `/members/${enc(email)}${rest}`);
export const memberRolePath = (orgId, email) => memberPath(orgId, email, '/role');
export const downloadsPath = (orgId) => orgPath(orgId, '/downloads');
export const downloadLinkPath = (orgId, releaseId) => orgPath(orgId, `/downloads/${enc(releaseId)}/link`);

/** Starts a browser download from a short-lived signed link path returned by the API. */
export const startDownload = (path) => {
  window.location.assign(`${process.env.REACT_APP_API_BASE_URL}/${String(path).replace(/^\//, '')}`);
};

/** Header carrying the emailed code that confirms an action on another IT admin. */
export const CONFIRM_CODE_HEADER = 'X-Confirm-Code';

/** True for errors the screens shouldn't toast: an ended session (already redirecting) or a cancelled confirmation. */
export const isQuietError = (error) => error?.status === 401 || error?.code === 'Cancelled';
export const adminOrgPath = (orgId) => (orgId ? `admin/orgs/${enc(orgId)}` : 'admin/orgs');
